import { appConfig } from '../config/appConfig';
import { createHeaders } from '../utilities/helper';
const BASE_URL = appConfig.api.baseUrl;

const createMultipartHeaders = (token: any) => {
  const headers = new Headers();
  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }
  return headers;
};

const buildOnboardingFormData = (payload: any) => {
  const formData = new FormData();
  const { approval_email, ...payloadWithoutFile } = payload;

  formData.append('payload', JSON.stringify(payloadWithoutFile));
  if (approval_email instanceof File) {
    formData.append('approval_email', approval_email);
  }

  return formData;
};

const getFilenameFromContentDisposition = (contentDisposition: string | null) => {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || null;
};

const getErrorMessageFromResponse = async (response: Response, fallback: string) => {
  try {
    const body = await response.json();
    return body?.error || body?.message || body?.details || fallback;
  } catch {
    return fallback;
  }
};

const apiService = {
  get: async (endpoint: string, token: any) => {
    const response = await fetch(BASE_URL + `/${endpoint}`, {
      headers: createHeaders(token)
    });
    switch (response.status) {
      case 200:
      case 201: {
        const data = await response.json();
        return data;
      }
      case 400:
      case 403:
        throw new Error('Client Error: There was an issue with your request.');
      case 500:
        throw new Error('Server Error: There was an issue with the server.');
      case 401:
        throw new Error('UNAUTHORIZED');
      default:
        throw new Error(`Unexpected Error: Unexpected error Occured`);
    }
  },
  post: async (endpoint: string, payload: Object, token: any) => {
    const response = await fetch(BASE_URL + `/${endpoint}`, {
      method: 'POST',
      headers: createHeaders(token),

      body: JSON.stringify(payload)
    });

    switch (response.status) {
      case 200:
      case 201: {
        const data = await response.json();
        return data;
      }
      case 400:
        throw new Error(
          'Bad Request: The response was filtered due to the prompt triggering content management policy. Please modify your prompt and retry.'
        );
      case 403: {
        const { name } = await response.json();
        throw new Error(
          `Permission Denied: You do not have permission to query the endpoint. Name: ${name}`
        );
      }
      case 500:
        throw new Error('Server Error: There was an issue with the server');
      case 401:
        throw new Error('UNAUTHORIZED');
      default:
        throw new Error(`Unexpected Error: Unexpected error Occured`);
    }
  },
  put: async (endpoint: string, payload: Object, token: any) => {
    const response = await fetch(BASE_URL + `/${endpoint}`, {
      method: 'PUT',
      headers: createHeaders(token),
      body: JSON.stringify(payload)
    });

    switch (response.status) {
      case 200:
      case 201: {
        const data = await response.json();
        return data;
      }
      case 400:
        throw new Error(
          await getErrorMessageFromResponse(response, 'Bad Request: Invalid payload or request.')
        );
      case 403:
        throw new Error(
          await getErrorMessageFromResponse(
            response,
            'Permission Denied: You do not have permission.'
          )
        );
      case 500:
        throw new Error(
          await getErrorMessageFromResponse(
            response,
            'Server Error: There was an issue with the server'
          )
        );
      case 401:
        throw new Error('UNAUTHORIZED');
      default:
        throw new Error(`Unexpected Error: ${response.statusText}`);
    }
  },
  postFormData: async (endpoint: string, payload: FormData, token: any) => {
    const response = await fetch(BASE_URL + `/${endpoint}`, {
      method: 'POST',
      headers: createMultipartHeaders(token),
      body: payload
    });

    switch (response.status) {
      case 200:
      case 201: {
        const data = await response.json();
        return data;
      }
      case 400:
        throw new Error(
          await getErrorMessageFromResponse(response, 'Bad Request: Invalid payload or request.')
        );
      case 403:
        throw new Error(
          await getErrorMessageFromResponse(
            response,
            'Permission Denied: You do not have permission.'
          )
        );
      case 500:
        throw new Error(
          await getErrorMessageFromResponse(
            response,
            'Server Error: There was an issue with the server'
          )
        );
      case 401:
        throw new Error('UNAUTHORIZED');
      default:
        throw new Error(`Unexpected Error: ${response.statusText}`);
    }
  },
  putFormData: async (endpoint: string, payload: FormData, token: any) => {
    const response = await fetch(BASE_URL + `/${endpoint}`, {
      method: 'PUT',
      headers: createMultipartHeaders(token),
      body: payload
    });

    switch (response.status) {
      case 200:
      case 201: {
        const data = await response.json();
        return data;
      }
      case 400:
        throw new Error('Bad Request: Invalid payload or request.');
      case 403:
        throw new Error('Permission Denied: You do not have permission.');
      case 500:
        throw new Error('Server Error: There was an issue with the server');
      case 401:
        throw new Error('UNAUTHORIZED');
      default:
        throw new Error(`Unexpected Error: ${response.statusText}`);
    }
  },
  patch: async (endpoint: string, payload: Object, token: any) => {
    const response = await fetch(BASE_URL + `/${endpoint}`, {
      method: 'PATCH',
      headers: createHeaders(token),
      body: JSON.stringify(payload)
    });

    switch (response.status) {
      case 200:
      case 201: {
        const data = await response.json();
        return data;
      }
      case 400:
        throw new Error('Bad Request: Invalid payload or request.');
      case 403:
        throw new Error('Permission Denied: You do not have permission.');
      case 500:
        throw new Error('Server Error: There was an issue with the server');
      case 401:
        throw new Error('UNAUTHORIZED');
      default:
        throw new Error(`Unexpected Error: ${response.statusText}`);
    }
  },
  delete: async (endpoint: string, token: any) => {
    const response = await fetch(BASE_URL + `/${endpoint}`, {
      method: 'DELETE',
      headers: createHeaders(token)
    });
    switch (response.status) {
      case 200:
      case 201: {
        const data = await response.json();
        return data;
      }
      case 400:
      case 403:
        throw new Error('Client Error: There was an issue with your request.');
      case 500:
        throw new Error('Server Error: There was an issue with the server.');
      case 401:
        throw new Error('UNAUTHORIZED');
      default:
        throw new Error(`Unexpected Error: Unexpected error Occured`);
    }
  },
  download: async (endpoint: string, token: any) => {
    const response = await fetch(BASE_URL + `/${endpoint}`, {
      headers: createHeaders(token)
    });

    switch (response.status) {
      case 200: {
        const blob = await response.blob();
        const filename =
          getFilenameFromContentDisposition(response.headers.get('Content-Disposition')) ||
          'approval_email';
        return { blob, filename };
      }
      case 400:
      case 403:
        throw new Error('Client Error: There was an issue with your request.');
      case 404:
        throw new Error('NOT_FOUND');
      case 500:
        throw new Error('Server Error: There was an issue with the server.');
      case 401:
        throw new Error('UNAUTHORIZED');
      default:
        throw new Error(`Unexpected Error: Unexpected error Occured`);
    }
  }
};

export const fetchApplicationsData = (token: any) => {
  const data = apiService.get('application/', token);
  return data;
};

export const checkApplicationName = (appName: string, token: any) => {
  if (!appName) throw new Error('Application name is required');
  const endpoint = `onboarding/check_application_name?application_name=${encodeURIComponent(
    appName
  )}`;
  return apiService.get(endpoint, token);
};

export const fetchModelUsage = (token: any) => {
  const data = apiService.get('model_usage/', token);
  return data;
};
export const fetchRaiApplicationsData = (token: any) => {
  const data = apiService.get('application/?rai=true', token);
  return data;
};

export const fetchModelsData = (appid: any, token: any) => {
  const data = apiService.get(`model/?client_id=${appid}`, token);
  return data;
};

export const fetchModelStatus = (token: any) => {
  const data = apiService.get('application_model/', token);
  return data;
};

export const fetchKPIS = ({ appID, modelID }: any, token: any) => {
  let data;

  if (appID && !modelID) data = apiService.get(`llm_ops_dashboard/?application_id=${appID}`, token);
  else if (modelID && appID)
    data = apiService.get(`llm_ops_dashboard/?application_id=${appID}&model_id=${modelID}`, token);
  else {
    data = apiService.get(`llm_ops_dashboard/`, token);
  }
  return data;
};

export const fetchGraphDetails = ({ modelID }: any, token: any) => {
  let data;
  if (!modelID) {
    data = apiService.get(`metrics/`, token);
  } else if (modelID) {
    data = apiService.get(`metrics/?model_id=${modelID}`, token);
  }
  return data;
};

export const fetchLeaderboardData = (token: any) => {
  const data = apiService.get('leaderboard/', token);
  return data;
};

export const fetchAllPrompt = (token: any) => {
  const data = apiService.get('prompt_lib/', token);
  return data;
};

export const fetchAllPromptDetails = (id: string, token: any) => {
  const data = apiService.get(`prompt_lib_new/${id}/versions`, token);
  return data;
};

export const fetchAllPrompt_new = (token: any) => {
  const data = apiService.get('prompt_lib_new/', token);
  return data;
};

export const fetchMyPrompt_new = (token: any) => {
  const data = apiService.get('my_prompts/', token);
  return data;
};

export const fetchMyPromptDetails = (query: string, token: any) => {
  const data = apiService.get(`my_prompts/${query}`, token);
  return data;
};

export const checkModelNameDuplication = (token: any, name: string) => {
  const data = apiService.get(`onboarding/check_endpoint_name?endpoint_name=${name}`, token);
  return data;
};

export const fetchPromptLibrarySuggestion = (query: any, token: any) => {
  const data = apiService.post('/submit_page/suggest', query, token);

  return data;
};

export const editChat = (payload: any, token: any) => {
  const data = apiService.post('playground_chat/edit_last_turn', payload, token);

  return data;
};

export const deleteMyPrompt = (
  query: {
    promptID: string;
    versionID: string;
    radiobtn: { id: number; text: string; checked: boolean }[];
  },
  token: any
) => {
  const { promptID, versionID, radiobtn } = query;
  const data = apiService.delete(
    `my_prompts/${promptID}/versions/${versionID}?deleteMaster=${radiobtn[0]?.checked}&deletVersion=${radiobtn[1]?.checked}`,
    token
  );
  return data;
};

export const AddPromptAPI = (query: Object, token: any) => {
  const data = apiService.post(`submit_page/`, query, token);
  return data;
};

export const EditPromptAPI = (
  query: Object,
  id: string,
  selectedVersion: number,
  isNew: boolean,
  token: any
) => {
  const update = `my_prompts/${id}?mode=update&base_version_no=${selectedVersion}`;
  const newVersion = `my_prompts/${id}?`;
  const data = apiService.put(isNew ? newVersion : update, query, token);
  return data;
};

export const clonePromptAPI = (
  query: { promptID: string; versionID: string },
  promptNameField: string,
  token: any
) => {
  const { promptID, versionID } = query;
  const data = apiService.post(
    `prompt_lib_new/${promptID}/clone?versionID=${versionID}`,
    { prompt_name: promptNameField },
    token
  );
  return data;
};

export const fetchPromptById = (id: string, token: any) => {
  const data = apiService.get(`prompt_lib/?prompt_id=${id}`, token);
  return data;
};

export const postUserQuery = (query: Object, token: any) => {
  const data = apiService.post(`prompt_lib/`, query, token);
  return data;
};
export const createPrompt = (query: Object, token: any) => {
  const data = apiService.post(`prompt/`, query, token);
  return data;
};

export const fetchRaiModelStatus = async (token: any) => {
  const data = apiService.get('model_status/', token);
  return data;
};

export const fetchRaiKPIs = async ({ appId }: any, token: any) => {
  let data;
  if (appId) {
    data = apiService.get(`rai_usage_metrics/?application_id=${appId}`, token);
  } else {
    data = apiService.get('rai_usage_metrics/', token);
  }

  return data;
};

export const fetchRAIBatchMetrics = ({ appId }: any, token: any) => {
  let data;
  if (appId) {
    data = apiService.get(`rai_batch_metrics/?application_id=${appId}`, token);
    return data;
  } else throw new Error('App id required');
};

export const fetchRAISafetyMetrics = ({ appId }: any, token: any) => {
  let data;
  if (appId) {
    data = apiService.get(`rai_safety_metrics/?application_id=${appId}`, token);
    return data;
  } else throw new Error('App id required');
};

export const postPlayground = (query: any, token: any) => {
  const data = apiService.post(`playground/`, query, token);
  return data;
};

export const fetchAllModels = (token: any) => {
  const data = apiService.get('playground/', token);
  return data;
};

export const fetchPlaygroundHistory = (token: any) => {
  const data = apiService.get('playground_history/history', token);
  return data;
};

export const comprarePromptHistoryAPI = (token: any) => {
  const data = apiService.get('compare_playground/runs', token);
  return data;
};

export const creditAPI = (token: any) => {
  const data = apiService.get('application_credit/credits', token);
  return data;
};

export const runHistoryAPI = (token: any, id: string) => {
  const data = apiService.get(`compare_playground/${id}`, token);
  return data;
};

export const compareRunAPI = async (token: any, payload: any) => {
  try {
    const data = await apiService.post('compare_playground', payload, token);
    console.log(data, 'ddataxxta');
    return data;
  } catch (err) {
    console.error('compareRunAPI error:', err);
    throw err;
  }
};

export const fetchIndexes = (token: any, selectedApplication: string) => {
  const data = apiService.get(`rag_data?application_name=${selectedApplication}`, token);
  return data;
};

export const fetchModels = (token: any) => {
  const data = apiService.get('playground_history/llms', token);
  return data;
};

export const chat = (token: any, payload: any) => {
  const data = apiService.post('playground_chat/run', payload, token);
  return data;
};

export const playgroundSavePrompt = (token: any, payload: any) => {
  const data = apiService.post('/playground_save/save_from_lib', payload, token);
  return data;
};

export const previousMessages = (token: any, payload: any) => {
  const data = apiService.get(`playground_history/history/${payload}`, token);
  return data;
};

export const fetchAdminCheck = (token: any) => {
  const data = apiService.get('admin/', token);
  return data;
};

export const fetchCostforApps = ({ startDate, endDate, appId }: any, token: any) => {
  let data;

  if (startDate && endDate) {
    if (appId) {
      data = apiService.get(
        `llm_usage_cost/?startDate=${startDate}&endDate=${endDate}&client_id=${appId}`,
        token
      );
    } else
      data = apiService.get(`llm_usage_cost/?startDate=${startDate}&endDate=${endDate}`, token);
  }
  return data;
};
export const postFeedback = (feedback: any, token: any) => {
  const data = apiService.post('feedback/', feedback, token);
  return data;
};

export const fetchPromptModels = (appId: any, token: any) => {
  const data = apiService.get(`prompt_app_models/?client_id=${appId}`, token);
  return data;
};

export const onboardingApplication = (payload: any, token: any) => {
  const data =
    payload?.approval_email instanceof File
      ? apiService.postFormData(`onboarding/`, buildOnboardingFormData(payload), token)
      : apiService.post(`onboarding/`, payload, token);
  return data;
};

export const getAppList = (token: any, spn?: string) => {
  const endpoint = `onboarding/${spn ?? ''}`;
  const data = apiService.get(endpoint, token);
  return data;
};

export const deletePlaygroundHistoryAPI = (token?: string, playground_id?: string) => {
  const data = apiService.delete(`playground_chat_delete/runs/${playground_id}`, token);
  return data;
};

export const deleteCompareHistory = (token?: string, id: string) => {
  const data = apiService.delete(`compare_playground/${id}`, token);
  return data;
};

export const updateOnboardingApplication = (onboardingId: number, payload: any, token: any) => {
  // console.log('Updating with onboardingId:', onboardingId);
  return payload?.approval_email instanceof File
    ? apiService.putFormData(`onboarding/${onboardingId}`, buildOnboardingFormData(payload), token)
    : apiService.put(`onboarding/${onboardingId}`, payload, token);
};

export const patchOnboardingStatus = (
  onboardingId: number,
  payload: { status: 'Approved' | 'Rejected' | 'Deleted'; comment?: string },
  token: any
) => {
  return apiService.patch(`onboarding/${onboardingId}`, payload, token);
};

export const downloadApprovalEmail = (onboardingId: number, token: any) => {
  return apiService.download(`onboarding/${onboardingId}/approval_email/download`, token);
};

export const getModelsForOnboardingApps = (token: any) => {
  const data = apiService.get(`model/base`, token);
  return data;
};

export const getAppIdandSPNData = (token: any) => {
  const data = apiService.get('application_identity/', token);
  return data;
};

export const getBusinessCategories = (token: any) => {
  const data = apiService.get('/submit_page/business_category', token);
  return data;
};

export const checkPromptNameAvailability = (token: any, name: string) => {
  const data = apiService.get(`/submit_page/prompt_name/check?prompt_name=${name}`, token);
  return data;
};

export const fetchPricingDetails = (token: any) => {
  const data = apiService.get('/pricing_model/', token);
  return data;
};

export const fetchTiers = (token: any) => {
  const data = apiService.get('onboarding/gettiers', token);
  return data;
};

export const fetchSystemAnalyticsData = (token: any, filters: Record<string, any> = {}) => {
  const params = new URLSearchParams();

  if (filters.cloud_platform_id) params.append('cloud_platform_id', filters.cloud_platform_id);

  // Map the filter keys to expected API parameters
  if (filters.application) params.append('application', filters.application);
  if (filters.environment) params.append('environment', filters.environment);
  if (filters.modelName) params.append('modelName', filters.modelName);
  if (filters.costType) params.append('cost_type', filters.costType);

  // Handle date range fields (if present)
  if (filters.timePeriod?.from) params.append('start_date', filters.timePeriod.from);
  if (filters.timePeriod?.to) params.append('end_date', filters.timePeriod.to);

  const queryString = params.toString();
  const url = queryString
    ? `analytics/dashboard_combined?${queryString}`
    : `analytics/dashboard_combined`;

  const data = apiService.get(url, token);
  return data;
};

export const fetchUnifiedDashboardKpis = (token: any, filters: Record<string, any> = {}) => {
  const params = new URLSearchParams();

  if (filters.cloud_platform_id) params.append('cloud_platform_id', filters.cloud_platform_id);
  if (filters.env) params.append('env', filters.env);
  if (filters.application) params.append('application', filters.application);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);

  const queryString = params.toString();
  const url = queryString ? `unified_dashboard/kpis?${queryString}` : `unified_dashboard/kpis`;

  return apiService.get(url, token);
};

export const fetchCostAnalyticsData = (token: any, filters: Record<string, any> = {}) => {
  const params = new URLSearchParams();
  if (filters.cloud_platform_id) params.append('cloud_platform_id', filters.cloud_platform_id);
  // Map the filter keys to expected API parameters
  if (filters.application) params.append('application', filters.application);
  if (filters.environment) params.append('environment', filters.environment);
  if (filters.modelName) params.append('modelName', filters.modelName);
  if (filters.costType) params.append('cost_type', filters.costType);

  // Handle date range fields (if present)
  if (filters.timePeriod?.from) params.append('start_date', filters.timePeriod.from);
  if (filters.timePeriod?.to) params.append('end_date', filters.timePeriod.to);

  const queryString = params.toString();
  const url = queryString
    ? `cost_analytics/dashboard_combined_cost?${queryString}`
    : `cost_analytics/dashboard_combined_cost`;

  const data = apiService.get(url, token);
  return data;
};

export const systemAnalyticsCardsData = (token: any) => {
  const data = apiService.get(`analytics/cards`, token);
  return data;
};

export const systemFilterDropdownData = (token: any) => {
  const data = apiService.get(`analytics/filters`, token);
  return data;
};

export const costAnalyticsCardsData = (token: any) => {
  const data = apiService.get(`cost_analytics/metrics`, token);
  return data;
};

export const costAnalyticsFilterDropdownData = (token: any) => {
  const data = apiService.get(`cost_analytics/filters`, token);
  return data;
};

export const costAnalyticsTableData = (token: any) => {
  const data = apiService.get(`cost_analytics/application_table`, token);
  return data;
};
export const modelOnboarding = (payload: any, token: any) => {
  const data = apiService.post(`onboarding/model_onboarding_new`, payload, token);
  return data;
};

export const modelOnboardingTrigger = (payload: any, token: any) => {
  const data = apiService.post(`onboarding/trigger_pipeline`, payload, token);
  return data;
};

export const updateModelOnboarding = (payload: any, token: any, editModelId: number) => {
  const data = apiService.put(`onboarding/model_onboarding_new/${editModelId}`, payload, token);
  return data;
};

export const fetchModelsOnboarding = (token: any) => {
  const data = apiService.get(`onboarding/model_onboarding_new`, token);
  return data;
};
export const ethicsAnalyticsCardsData = (token: any, filters: Record<string, any> = {}) => {
  const params = new URLSearchParams();
  if (filters.cloud_platform_id) params.append('cloud_platform_id', filters.cloud_platform_id);
  // Map the filter keys to expected API parameters
  if (filters.application) params.append('application', filters.application);
  if (filters.environment) params.append('environment', filters.environment);
  if (filters.modelName) params.append('modelName', filters.modelName);
  if (filters.costType) params.append('cost_type', filters.costType);

  // Handle date range fields (if present)
  if (filters.timePeriod?.from) params.append('start_date', filters.timePeriod.from);
  if (filters.timePeriod?.to) params.append('end_date', filters.timePeriod.to);

  const queryString = params.toString();
  const url = queryString ? `ethics/analytics?${queryString}` : `ethics/analytics`;

  const data = apiService.get(url, token);
  return data;
};

export const filterDropdownData = (token: any, app: string, cloud_platform_id: number) => {
  var tempUrl = `ethics/filters`;

  if (app) {
    tempUrl = tempUrl + `?application=${app}`;
  }
  if (cloud_platform_id) {
    tempUrl = tempUrl + `&cloud_platform_id=${cloud_platform_id}`;
  }

  const url = tempUrl;
  const data = apiService.get(url, token);
  return data;
};

export const filterDropdownDataUnified = (token: any, env: string, cloud_platform_id: number) => {
  const params = new URLSearchParams();

  if (env) params.append("env", env);

  if (cloud_platform_id) params.append("cloud_platform_id", String(cloud_platform_id));

  const url = `unified_dashboard/filters${params.toString() ? `?${params.toString()}` : ""}`;

  const data =  apiService.get(url, token);
  return data;
};

// export const filterDropdownDataCloudProvider = (
//   token: any,
//   app: string,
//   cloud_platform_id: number
// ) => {
//   var tempUrl = `ethics/filters`;
export const filterDropdownDataCloudProvider = (
  token: any,
  app: string,
  cloud_platform_id: number
) => {
  var tempUrl = `ethics/filters`;

  if (cloud_platform_id) {
    tempUrl = tempUrl + `?cloud_platform_id=${cloud_platform_id}`;
  }
  if (app) {
    tempUrl = tempUrl + `&application=${app}`;
  }

  const url = tempUrl;
  const data = apiService.get(url, token);
  return data;
};

export const fetchRagOboardingDetilas = (token: any) => {
  const data = apiService.get(`rag_builder/onboardings`, token);
  return data;
};

export const fetchHealthKpis = (token: any, filters: any) => {
  const params = new URLSearchParams();
  if (filters.application) params.append('model_type', filters.application);
  if (filters.modelType) params.append('provider', filters.modelType);
  // if (filters.application) params.append('provider', filters.application);
  // if (filters.modelType) params.append('model_type', filters.modelType);
  if (filters.env) params.append('env', filters.env);
  const queryString = params.toString();
  const url = queryString ? `health/kpi/summary?${queryString}` : `health/kpi/summary`;

  const data = apiService.get(url, token);
  return data;

  // const data = apiService.get(`health/kpi/summary`, token);
  // return data;
};

export const fetchHealthTimeline = (token: any, filters: any) => {
  const params = new URLSearchParams();
  if (filters.application) params.append('model_type', filters.application);
  if (filters.modelType) params.append('provider', filters.modelType);
  if (filters.env) params.append('env', filters.env);
  const queryString = params.toString();
  const url = queryString ? `health/timeline?${queryString}` : `health/timeline`;

  const data = apiService.get(url, token);
  return data;

  // const data = apiService.get(`health/timeline`, token);
  // return data;
};

export const fetchEndpointStatus = (token: any, filters: any) => {
  const params = new URLSearchParams();
  if (filters.application) params.append('model_type', filters.application);
  if (filters.modelType) params.append('provider', filters.modelType);
  if (filters.env) params.append('env', filters.env);
  const queryString = params.toString();
  const url = queryString ? `health/endpoint-status?${queryString}` : `health/endpoint-status`;

  const data = apiService.get(url, token);
  return data;
};

export const refreshAllEnv = (payload: any, token: any) => {
  const data = apiService.post(`health/run_all`, payload, token);
  return data;
};

export const refreshSpecificEnv = (payload: any, token: any) => {
  const data = apiService.post(`health/run_env`, payload, token);
  return data;
};

export const refreshSingleEnv = (payload: any, token: any) => {
  const data = apiService.post(`health/run_single`, payload, token);
  return data;
};
